/* ==============================================
   JSON-LD.

   "RAIN" is a crowded name — a fintech, RAIN Group,
   Raines International, RAIN Career Institute and
   Rain AI all compete for it, and for "RAIN careers"
   this site sits behind Indeed and its own MIT
   listing. Structured data is the strongest signal
   we can send about which RAIN this is, so the
   home page carries a SoftwareApplication with the
   publisher and social profiles attached.

   Articles carry their own Article node so a piece
   can surface with its title, cover and read time
   rather than as a bare blue link.
   ============================================== */

import { Helmet } from "react-helmet-async";

import { SITE, absoluteUrl } from "./site";

const Ld = ({ data }) => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(data)}</script>
  </Helmet>
);

const publisher = {
  "@type": "Organization",
  name: SITE.legalName,
  url: SITE.origin,
  logo: absoluteUrl(SITE.logo),
  sameAs: SITE.sameAs,
};

export const SoftwareApplicationSchema = () => (
  <Ld
    data={{
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: SITE.name,
      alternateName: "RAIN Careers",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: SITE.origin,
      description:
        "AI-powered job search platform for college students and recent graduates.",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher,
    }}
  />
);

/* The site-level Organization node, so the brand itself is described once
   and independently of the product. Lives on the home page beside the
   SoftwareApplication. */
export const OrganizationSchema = () => (
  <Ld
    data={{
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE.legalName,
      alternateName: ["RAIN", "RAIN Careers"],
      url: SITE.origin,
      logo: absoluteUrl(SITE.logo),
      sameAs: SITE.sameAs,
    }}
  />
);

export const ArticleSchema = ({ title, description, image, path, datePublished }) => (
  <Ld
    data={{
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title,
      description,
      image: image ? [absoluteUrl(image)] : undefined,
      mainEntityOfPage: { "@type": "WebPage", "@id": absoluteUrl(path) },
      author: publisher,
      publisher,
      ...(datePublished ? { datePublished } : {}),
    }}
  />
);

/* Trail for an article: Home › Insights › the piece. Gives Google the
   breadcrumb row under the result and reinforces that /insights is a
   section rather than a loose page. */
export const BreadcrumbSchema = ({ trail = [] }) => (
  <Ld
    data={{
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: trail.map(({ name, path }, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name,
        item: absoluteUrl(path),
      })),
    }}
  />
);
